import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';

function usePublicUserId() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw)?.id || null;
  } catch {
    return null;
  }
}

const ARTICLES = {
  'project-basics': {
    title: 'How a landscape project usually works',
    body: (
      <>
        <p className="text-gray-700 leading-relaxed">
          Most yard projects move through a few predictable phases. You do not need to memorize jargon, but knowing the
          sequence helps you budget time, compare quotes, and avoid surprises.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">1. First conversation</h2>
        <p className="mt-3 text-gray-700 leading-relaxed">
          You share goals (privacy, play space, low maintenance, drainage), constraints (budget range, timeline), and
          photos or rough measurements. A designer or contractor listens for what you need versus what you might be
          asking for in one jump.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">2. Site visit and assessment</h2>
        <p className="mt-3 text-gray-700 leading-relaxed">
          Someone walks the property to note sun and shade, slopes, utilities, existing plants worth keeping, and
          access for equipment. This step turns wish lists into something that fits your actual yard.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">3. Concept and plan</h2>
        <p className="mt-3 text-gray-700 leading-relaxed">
          You may see sketches, mood references, or a simple plan showing beds, paths, and key plants. Feedback here is
          cheaper than changes after materials are ordered.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">4. Refinement and scope</h2>
        <p className="mt-3 text-gray-700 leading-relaxed">
          The plan tightens into what will be built: plant list, hardscape notes, and phases if you are splitting work
          over seasons. You should see how allowances, contingencies, and irrigation or lighting are handled.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">5. Installation</h2>
        <p className="mt-3 text-gray-700 leading-relaxed">
          Crews prepare soil, set hardscape, place plants, and set up irrigation if included. Weather and hidden site
          issues can shift timing; good teams communicate delays early.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">6. Handoff and care</h2>
        <p className="mt-3 text-gray-700 leading-relaxed">
          You get watering and pruning basics for new plants, warranty notes where applicable, and sometimes a follow-up
          visit after the first growing season. Keep one place (email or folder) for plans and plant names so future
          updates stay easy.
        </p>
      </>
    ),
  },
  'choosing-a-designer': {
    title: 'Choosing a designer you click with',
    body: (
      <>
        <p className="text-gray-700 leading-relaxed">
          Skill matters, but so does fit. You will be making decisions together for weeks or months. A little structure
          up front saves friction later.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">Clarify what you want help with</h2>
        <p className="mt-3 text-gray-700 leading-relaxed">
          Full design and project support differs from a one-time consult or a planting plan only. Say whether you need
          someone who manages install, refers contractors, or leaves you to hire your own crew.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">Ask how they work</h2>
        <ul className="mt-3 list-disc pl-6 text-gray-700 space-y-2">
          <li>How do they charge (flat fee, hourly, percentage of construction)?</li>
          <li>What is included in the first proposal versus later revisions?</li>
          <li>How do they handle changes once work starts?</li>
        </ul>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">Look for relevant experience</h2>
        <p className="mt-3 text-gray-700 leading-relaxed">
          Similar climate, lot size, and problem types (steep slopes, deer, small urban lots) matter as much as a large
          portfolio. Ask for one or two projects that faced constraints like yours.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">Communication style</h2>
        <p className="mt-3 text-gray-700 leading-relaxed">
          Do you want frequent check-ins or summaries at milestones? Make that explicit. Misaligned expectations on
          updates cause more stress than plant choices.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">Green flags</h2>
        <ul className="mt-3 list-disc pl-6 text-gray-700 space-y-2">
          <li>They listen before pitching a signature look.</li>
          <li>They explain tradeoffs (budget, timing, maintenance) in plain language.</li>
          <li>They are clear about what they need from you and by when.</li>
        </ul>
        <p className="mt-6 text-gray-700 leading-relaxed">
          PlantADay can help you discover professionals in your area; use{' '}
          <Link to="/find-designer" className="text-green-700 font-medium hover:underline">
            Find a designer
          </Link>{' '}
          when you are ready to compare options.
        </p>
      </>
    ),
  },
  'seasonal-tips': {
    title: 'Quick seasonal tips',
    body: (
      <>
        <p className="text-gray-700 leading-relaxed">
          You do not need a full redesign every season. Small, timely actions keep plants healthier and reduce emergency
          spending later.
        </p>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">Spring</h2>
        <ul className="mt-3 list-disc pl-6 text-gray-700 space-y-2">
          <li>Walk the yard after the soil drains: note frost heave, mulch thin spots, and winter damage on evergreens.</li>
          <li>Prune spring-blooming shrubs after bloom if they need shaping; avoid heavy cuts that remove this year&apos;s flowers.</li>
          <li>Refresh mulch before heat arrives, keeping it off crowns and trunks.</li>
        </ul>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">Summer</h2>
        <ul className="mt-3 list-disc pl-6 text-gray-700 space-y-2">
          <li>Water deeply and less often rather than light daily sprinkles, especially for new plantings.</li>
          <li>Weed before seeds drop; a little time now saves hours in fall.</li>
          <li>Photograph areas that struggle in heat—you will forget the details by planning season.</li>
        </ul>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">Fall</h2>
        <ul className="mt-3 list-disc pl-6 text-gray-700 space-y-2">
          <li>Plant evergreens and spring bulbs early enough for roots to settle before hard frost.</li>
          <li>Leave seed heads that feed birds; cut back diseased material only.</li>
          <li>Note drainage: ruts or pooling now predict mud and ice problems later.</li>
        </ul>
        <h2 className="mt-8 text-xl font-semibold text-gray-900">Winter</h2>
        <ul className="mt-3 list-disc pl-6 text-gray-700 space-y-2">
          <li>Protect young trunks from sunscald and rodents where that is a known issue in your area.</li>
          <li>Avoid heavy foot traffic on saturated lawn and beds.</li>
          <li>Use quiet months to plan next year: budget, priorities, and whether to bring in design help.</li>
        </ul>
        <p className="mt-8 text-gray-700 leading-relaxed">
          For plant ideas matched to your goals, try{' '}
          <Link to={{ pathname: '/ideas', hash: '#guided-lookup' }} className="text-green-700 font-medium hover:underline">
            Yard Ideas &amp; guided lookup
          </Link>
          .
        </p>
      </>
    ),
  },
};

export default function LearnArticleStub() {
  const { slug } = useParams();
  const currentUserId = usePublicUserId();
  const article = slug ? ARTICLES[slug] : null;

  if (!article) {
    return <Navigate to="/learn" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <main className="flex-1 max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <Link to="/learn" className="text-sm font-medium text-green-700 hover:underline">
          ← Back to Learn
        </Link>
        <article className="mt-6">
          <h1 className="text-3xl font-bold text-gray-900">{article.title}</h1>
          <div className="mt-6 max-w-none text-base space-y-4">{article.body}</div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
